/**
 * tabParams — the v2 console's URL model, and the redirect that keeps every
 * old link landing (CRM UI streamline D-7).
 *
 * The classic console published SEVEN `?tab=` values, and they are spread all
 * over the product: the Today worklist and the lead page's agent panel both
 * deep-link `?tab=queue&review=<convId>`, ContactDetail's escalation popups
 * send `?tab=escalations`, and the Monday weekly-review EMAIL — already in
 * people's inboxes, un-editable — sends `?tab=review`. v2 collapses those seven
 * into three (Needs you · Conversations · Manage, with Manage carrying
 * `sub=batches|tuning|review`), so every one of those links has to be caught and
 * forwarded rather than dumped on a default tab.
 *
 * That is the whole reason this is a pure module with its own tests instead of a
 * few lines inside the component: a redirect table is exactly the kind of thing
 * that silently rots when a tab is renamed, and the cost of it rotting is a
 * recruiter clicking a determination link and landing on an unrelated screen.
 *
 * The rules the tests pin down:
 * - `patch` preserves every OTHER param (`review=`, `conversation=`, anything a
 *   future link adds) — a redirect must never eat the payload it was carrying.
 * - `view=` is DROPPED on the board→conversations hop: the live grid is retired
 *   on v2, so carrying its param forward would leave a dead switch in the URL.
 * - Nothing is written that does not have to be: a bare `/crm/agent` stays bare
 *   (absent tab === Needs you), and an already-new URL comes back `changed:
 *   false` so the console never replaces the location in a loop.
 */

export const CONSOLE_TABS = ['needs', 'conversations', 'manage'] as const;
export type ConsoleTab = (typeof CONSOLE_TABS)[number];

export const MANAGE_SUBS = ['batches', 'tuning', 'review'] as const;
export type ManageSub = (typeof MANAGE_SUBS)[number];

/** Landing tab — absent or unrecognized `?tab=` both resolve here. */
export const DEFAULT_TAB: ConsoleTab = 'needs';
/** Manage's landing sub-panel — the one every CRM user can see. */
export const DEFAULT_SUB: ManageSub = 'batches';

/**
 * Every classic `?tab=` value → where it lands on v2. `sub` is set only where
 * the old value NAMED a Manage panel; `drop` lists params that stop meaning
 * anything after the hop.
 */
const REDIRECTS: Record<string, { tab: ConsoleTab; sub?: ManageSub; drop?: readonly string[] }> = {
  // The determination dashboard — merged into Needs you, `review=` intact.
  queue: { tab: 'needs' },
  // Both former triage queues are groups of the merged Needs-you list now.
  drafts: { tab: 'needs' },
  escalations: { tab: 'needs' },
  // The board keeps its `conversation=` selection; `view=grid` is retired.
  board: { tab: 'conversations', drop: ['view'] },
  batches: { tab: 'manage', sub: 'batches' },
  tuning: { tab: 'manage', sub: 'tuning' },
  // The weekly-review email links here (crmAgentReview.js) — un-editable once sent.
  review: { tab: 'manage', sub: 'review' },
};

export function isConsoleTab(value: string | null): value is ConsoleTab {
  return value !== null && (CONSOLE_TABS as readonly string[]).includes(value);
}

export function isManageSub(value: string | null): value is ManageSub {
  return value !== null && (MANAGE_SUBS as readonly string[]).includes(value);
}

export interface NormalizedConsoleParams {
  /** The tab to render. */
  tab: ConsoleTab;
  /** The Manage sub-panel to render (meaningful only when `tab === 'manage'`). */
  sub: ManageSub;
  /** `patch` differs from the input — the caller should write it back. */
  changed: boolean;
  /** The full param set the URL should hold, other params preserved. */
  patch: URLSearchParams;
}

/**
 * Resolve a console URL: which tab/sub it means, and (when it is an old-shaped
 * link) the params it should be rewritten to. Pure — the caller owns the write.
 */
export function normalizeConsoleParams(params: URLSearchParams): NormalizedConsoleParams {
  const rawTab = params.get('tab');
  const rawSub = params.get('sub');
  const redirect = rawTab === null ? undefined : REDIRECTS[rawTab];

  const tab: ConsoleTab = isConsoleTab(rawTab)
    ? rawTab
    : redirect
      ? redirect.tab
      : DEFAULT_TAB;

  /** The sub the URL explicitly asks for — null = "nothing valid in play". */
  const wantSub: ManageSub | null = redirect?.sub ?? (isManageSub(rawSub) ? rawSub : null);
  const sub: ManageSub = wantSub ?? DEFAULT_SUB;

  const patch = new URLSearchParams(params);
  // Write `tab` only when what is there is WRONG: a bare URL stays bare, an old
  // value is forwarded, junk falls back to the landing tab.
  if (rawTab !== null && rawTab !== tab) patch.set('tab', tab);
  if (wantSub !== null) {
    if (rawSub !== wantSub) patch.set('sub', wantSub);
  } else if (rawSub !== null && tab === 'manage') {
    // An unusable `sub` only matters where Manage would read it.
    patch.set('sub', DEFAULT_SUB);
  }
  for (const key of redirect?.drop ?? []) patch.delete(key);

  return { tab, sub, changed: patch.toString() !== params.toString(), patch };
}
